import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {orangeColors, neutral} from '../../../layouts/theme/designSystem';

type TestimonialKey = 'first' | 'second' | 'third';

type TestimonialItem = {
  key: TestimonialKey;
  imageUrl: string;
  imageHeight: {
    xs: number;
    md: number;
  };
};

const TESTIMONIAL_ITEMS: TestimonialItem[] = [
  {
    key: 'first',
    imageUrl: '/assets/b2b-testimonial-1.png',
    imageHeight: {xs: 240, md: 224},
  },
  {
    key: 'second',
    imageUrl: '/assets/b2b-testimonial-2.png',
    imageHeight: {xs: 260, md: 270},
  },
  {
    key: 'third',
    imageUrl: '/assets/b2b-testimonial-3.png',
    imageHeight: {xs: 240, md: 224},
  },
];

export const CustomerSatisfactionSection = () => {
  const {t} = useTranslation();

  return (
    <Grid
      id='customer-satisfaction'
      width='100%'
      sx={{
        backgroundColor: '#fff5e8',
        py: {xs: 5, md: 7},
        px: {xs: 2, sm: 3},
        boxSizing: 'border-box',
        scrollMarginTop: {xs: '96px', md: '140px'},
      }}
    >
      <Box sx={{maxWidth: '1120px', mx: 'auto'}}>
        <Typography
          sx={{
            color: orangeColors[900],
            fontWeight: 500,
            fontSize: {xs: '1.125rem', md: '1.25rem'},
            lineHeight: 1.5,
            textAlign: 'center',
            textTransform: 'uppercase',
          }}
        >
          {t('b2b.customerSatisfactionSection.eyebrow')}
        </Typography>

        <Typography
          sx={{
            mt: 1,
            color: neutral['primary-black'],
            fontWeight: 700,
            fontSize: {xs: '2rem', sm: '2.25rem', md: '2.125rem'},
            lineHeight: 1.5,
            textAlign: 'center',
          }}
        >
          {t('b2b.customerSatisfactionSection.headline')}
        </Typography>

        <Typography
          sx={{
            mt: {xs: 1, md: 1.5},
            color: neutral['primary-black'],
            fontWeight: 500,
            fontSize: {xs: '1rem', sm: '1.125rem', md: '1.25rem'},
            lineHeight: 1.5,
            textAlign: 'center',
            textTransform: 'uppercase',
          }}
        >
          {t('b2b.customerSatisfactionSection.subtitle')}
        </Typography>

        <Box
          sx={{
            mt: {xs: 4, md: 6},
            display: 'grid',
            gap: {xs: 3, md: 4},
            gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))'},
          }}
        >
          {TESTIMONIAL_ITEMS.map(({key, imageUrl, imageHeight}) => (
            <Box key={key} sx={{position: 'relative', mx: 'auto', width: '100%', maxWidth: '320px'}}>
              <Box
                component='img'
                src={imageUrl}
                alt={t(`b2b.customerSatisfactionSection.testimonials.${key}.imageAlt`) ?? undefined}
                sx={{
                  width: '100%',
                  height: imageHeight,
                  objectFit: 'cover',
                  borderRadius: '5px',
                  display: 'block',
                  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                }}
              />

              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: 12,
                  transform: 'translateX(-50%)',
                  width: 'calc(100% - 24px)',
                  maxWidth: '244px',
                  px: 1.5,
                  py: 1,
                  borderRadius: '5px',
                  backgroundColor: '#ffffff',
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    color: neutral['primary-black'],
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                  }}
                >
                  {t(`b2b.customerSatisfactionSection.testimonials.${key}.quote`)}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    color: neutral['primary-black'],
                    fontWeight: 300,
                    fontSize: '0.625rem',
                    lineHeight: 1.5,
                  }}
                >
                  {t(`b2b.customerSatisfactionSection.testimonials.${key}.author`)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Grid>
  );
};
